import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Layers, Users, Building2, Brain, Cog, Code, 
  ChevronRight, ArrowRight, CheckCircle, Sparkles,
  UserPlus, Send, Loader2, Globe, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { PageSEO, SEO_CONFIG, BreadcrumbSchema } from '../lib/seo';
import { siteConfig } from '../config/siteConfig';

const BRAND = { name: siteConfig.company.name };
import axios from 'axios';
import Typewriter from 'typewriter-effect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Layers: Layers,
  Users: Users,
  Building2: Building2,
  Brain: Brain,
  Cog: Cog,
  Code: Code,
};

// Skeleton Components
const ServiceCardSkeleton = () => (
  <div className="bg-white p-8 rounded-2xl border border-slate-100 animate-pulse">
    <div className="h-12 w-12 bg-slate-200 rounded-xl mb-6" />
    <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
    <div className="space-y-2">
      <div className="h-4 bg-slate-200 rounded w-full" />
      <div className="h-4 bg-slate-200 rounded w-5/6" />
    </div>
  </div>
);

// Hero Section with SEO-optimized content
const HeroSection = () => {
  const phrases = [
    'Custom Software Development',
    'Web Development Services',
    'Mobile App Development',
    'IT Consulting Solutions',
    'Digital Transformation',
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden" aria-label="Hero section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-warm-50 via-white to-warm-100" />
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23cc5500' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      
      {/* Decorative Elements */}
      <motion.div
        className="absolute top-20 right-20 h-72 w-72 rounded-full bg-primary/5 blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-10 h-96 w-96 rounded-full bg-secondary/5 blur-3xl"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm font-medium">Best IT Services Company</span>
            </motion.div>

            {/* SEO-optimized H1. Height reserved for worst-case 2-line typewriter
                phrase so the rest of the hero (description, buttons, trust row)
                doesn't shift vertically as the rotating text changes length. */}
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-6 leading-tight">
              <span className="block">Your Partner for</span>
              <span className="text-primary block min-h-[2.5em]">
                <Typewriter
                  options={{
                    strings: phrases,
                    autoStart: true,
                    loop: true,
                    deleteSpeed: 30,
                    delay: 50,
                  }}
                />
              </span>
            </h1>

            {/* SEO-rich description */}
            <p className="text-lg text-text-secondary mb-8 max-w-xl">
              {BRAND.name} is a leading <strong>IT services company</strong> specializing in 
              <strong> custom software development</strong>, <strong>web development</strong>, 
              and <strong>mobile app development</strong>. We help businesses worldwide transform digitally.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" onClick={scrollToTop}>
                <Button
                  data-testid="hero-cta-btn"
                  className="bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-glow transition-all"
                >
                  Get Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
              <Link to="/case-studies" onClick={scrollToTop}>
                <Button
                  data-testid="hero-secondary-btn"
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-accent rounded-full px-8 py-6 text-lg"
                >
                  View Our Work
                </Button>
              </Link>
            </div>

            {/* Trust Indicators — driven by siteConfig.locations */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <p className="text-sm text-text-muted mb-6">
                {siteConfig.locations.length > 1
                  ? `Serving clients across ${siteConfig.locations.length} regions`
                  : `Based in ${siteConfig.locations[0].country}, serving clients worldwide`}
              </p>
              <div className={`grid gap-4 ${siteConfig.locations.length === 1 ? 'grid-cols-1 max-w-xs' : siteConfig.locations.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {siteConfig.locations.slice(0, 3).map((loc, i) => (
                  <motion.div
                    key={`${loc.country}-${loc.city}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="flex flex-col items-center justify-center bg-slate-50 rounded-lg py-3 px-4 text-center"
                  >
                    <MapPin className="h-4 w-4 text-primary mb-1" aria-hidden="true" />
                    <span className="text-sm font-medium text-text-primary">{loc.country}</span>
                    <span className="text-xs text-text-muted">{loc.city}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Hero Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-3xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800"
                alt="Plutops software development team collaborating on IT projects"
                className="relative rounded-3xl shadow-2xl"
                loading="eager"
                width="800"
                height="600"
              />
              
              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 border border-slate-100"
              >
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Code className="h-6 w-6 text-primary" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-text-primary">10+</p>
                    <p className="text-sm text-text-muted">Projects Delivered</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section with SEO
const ServicesSection = ({ services, loading }) => {
  const getGridClass = (index) => {
    const patterns = [
      'md:col-span-2 md:row-span-1',
      'md:col-span-1 md:row-span-1',
      'md:col-span-1 md:row-span-1',
      'md:col-span-1 md:row-span-1',
      'md:col-span-1 md:row-span-1',
      'md:col-span-2 md:row-span-1',
    ];
    return patterns[index] || 'md:col-span-1';
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="py-24 lg:py-32 bg-white" aria-labelledby="services-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-primary font-medium mb-4">Our IT Services</span>
          <h2 id="services-heading" className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-text-primary mb-4">
            Comprehensive Software Development Services
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            From custom software development to IT consulting, we deliver end-to-end solutions 
            that help businesses grow and compete in the digital age.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <ServiceCardSkeleton key={i} />)
          ) : (
            services.map((service, index) => {
              const Icon = iconMap[service.icon] || Layers;
              return (
                <motion.article
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={getGridClass(index)}
                >
                  <Link to={`/services/${service.slug}`} onClick={scrollToTop} data-testid={`service-card-${service.slug}`}>
                    <div className="group h-full relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm border border-slate-100 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      
                      <div className="relative z-10">
                        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                          <Icon className="h-6 w-6 text-primary group-hover:text-white transition-colors" aria-hidden="true" />
                        </div>
                        
                        <h3 className="font-heading font-bold text-xl text-text-primary mb-3 group-hover:text-primary transition-colors">
                          {service.title}
                        </h3>
                        
                        <p className="text-text-secondary mb-4">
                          {service.description}
                        </p>
                        
                        <div className="flex items-center text-primary font-medium">
                          <span>Learn more</span>
                          <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

// Global Reach Section — driven by siteConfig.locations
const GlobalReachSection = () => {
  const locations = siteConfig.locations;
  const multi = locations.length > 1;

  return (
    <section className="py-24 bg-slate-50" aria-labelledby="global-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-primary font-medium mb-4">
            {multi ? 'Global Presence' : 'Our Office'}
          </span>
          <h2 id="global-heading" className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-4">
            {multi ? 'Where We Operate' : 'Where to Find Us'}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {multi
              ? `With offices in ${locations.map((l) => l.country).join(', ')}, we deliver services to clients worldwide.`
              : `Based in ${locations[0].city}, ${locations[0].country}, serving clients worldwide.`}
          </p>
        </motion.div>

        <div className={`grid gap-8 ${locations.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' : locations.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
          {locations.map((loc, index) => (
            <motion.article
              key={`${loc.country}-${loc.city}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Globe className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-text-primary">{loc.country}</h3>
                  <p className="text-sm text-text-muted">{loc.city}</p>
                </div>
              </div>
              {loc.description && (
                <p className="text-text-secondary mb-4">{loc.description}</p>
              )}
              <p className="text-xs text-text-muted">{loc.address}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

// How We Work — sticky scroll-stacking cards where each NEW card slides on top
// of all previous ones (z-index ascends with index, so latest-scrolled card wins).
const StepCard = ({ step, index }) => (
  <div
    className="sticky top-24"
    style={{ zIndex: index + 1 }}
  >
    <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12 border border-slate-100">
      <div className="flex flex-col md:flex-row md:items-center gap-8">
        <div className={`${step.color} h-20 w-20 rounded-2xl flex items-center justify-center shrink-0`}>
          <span className="font-heading font-bold text-2xl text-white">{step.number}</span>
        </div>
        <div>
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-3">
            {step.title}
          </h3>
          <p className="text-text-secondary text-lg">
            {step.description}
          </p>
        </div>
      </div>
    </div>
  </div>
);

const HowWeWorkSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Discovery & Strategy',
      description: 'We analyze your business requirements to create a comprehensive software development roadmap tailored to your goals.',
      color: 'bg-primary',
    },
    {
      number: '02',
      title: 'Design & Architecture',
      description: 'Our expert architects design scalable, secure software solutions with user-centric interfaces.',
      color: 'bg-secondary',
    },
    {
      number: '03',
      title: 'Development & Testing',
      description: 'Agile software development with continuous integration, ensuring high-quality, bug-free code delivery.',
      color: 'bg-primary-light',
    },
    {
      number: '04',
      title: 'Launch & Support',
      description: 'Smooth deployment with ongoing maintenance and 24/7 support to ensure long-term success.',
      color: 'bg-primary',
    },
  ];

  return (
    <section className="relative bg-white" aria-labelledby="process-heading">
      <div className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-primary font-medium mb-4">Our Software Development Process</span>
            <h2 id="process-heading" className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-text-primary mb-4">
              How We Build Your Software
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              A proven agile methodology that delivers quality software solutions on time and within budget.
            </p>
          </motion.div>

          {/* space-y gives each card vertical breathing room as you scroll past it
              before the next one slides up and stacks on top */}
          <div className="space-y-[60vh] max-w-4xl mx-auto pb-[60vh]">
            {steps.map((step, index) => (
              <StepCard
                key={step.number}
                step={step}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Lead Capture Section
const LeadCaptureSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    talents_required: '',
    roles_required: '',
    preferred_persona: '',
    budget: '',
    additional_info: '',
    full_name: '',
    work_email: '',
    phone: '',
    honeypot: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const roles = [
    'Full-Stack Developer',
    'Frontend Developer',
    'Backend Developer',
    'DevOps Engineer',
    'AI/ML Engineer',
    'Data Scientist',
    'Project Manager',
    'UI/UX Designer',
    'QA Engineer',
    'Other'
  ];

  const budgetRanges = [
    '$5K - $10K per month',
    '$10K - $25K per month',
    '$25K - $50K per month',
    '$50K+ per month'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        talents_required: parseInt(formData.talents_required)
      });
      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch shortly.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-24 lg:py-32 bg-gradient-to-br from-primary to-primary-hover" aria-label="Success message">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-white" aria-hidden="true" />
            </div>
            <h2 className="font-heading font-bold text-3xl text-white mb-4">
              Request Received!
            </h2>
            <p className="text-white/80 mb-8">
              Our team will review your requirements and get back to you within 24 hours.
            </p>
            <Button
              onClick={() => setSubmitted(false)}
              className="bg-white text-primary hover:bg-warm-100 rounded-full"
            >
              Submit Another Request
            </Button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="hire-talent" className="py-24 lg:py-32 bg-gradient-to-br from-primary to-primary-hover relative overflow-hidden" aria-labelledby="hire-heading">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 h-64 w-64 rounded-full bg-white blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-white blur-3xl" />
      </div>
      
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/20 text-white px-4 py-2 rounded-full mb-6">
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm font-medium">Hire Software Developers</span>
            </div>
            
            <h2 id="hire-heading" className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Hire Top Software Developers
            </h2>
            
            <p className="text-lg text-white/80 mb-8">
              Access pre-vetted, skilled software developers ready to build your app or website. 
              Staff augmentation services from {BRAND.name} with flexible engagement models.
            </p>
            
            <div className="space-y-4">
              {[
                'Pre-vetted senior developers',
                'No recruitment overhead',
                'Flexible engagement models',
                'Direct communication'
              ].map((benefit, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-white/80" aria-hidden="true" />
                  <span className="text-white/90">{benefit}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-8" aria-label="Hire developers form">
              <input type="text" name="honeypot" value={formData.honeypot} onChange={handleChange} className="hidden" tabIndex={-1} autoComplete="off" />
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="talents_required" className="text-text-primary">Number of Developers *</Label>
                  <Input
                    id="talents_required"
                    type="number"
                    name="talents_required"
                    data-testid="lead-talents-input"
                    value={formData.talents_required}
                    onChange={handleChange}
                    required
                    min="1"
                    placeholder="e.g., 3"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="roles_required" className="text-text-primary">Role Required *</Label>
                  <Select
                    value={formData.roles_required}
                    onValueChange={(value) => handleSelectChange('roles_required', value)}
                    required
                  >
                    <SelectTrigger id="roles_required" data-testid="lead-roles-select" className="h-12">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="preferred_persona" className="text-text-primary">Experience Level</Label>
                  <Input
                    id="preferred_persona"
                    type="text"
                    name="preferred_persona"
                    value={formData.preferred_persona}
                    onChange={handleChange}
                    placeholder="e.g., Senior, 5+ years"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-text-primary">Budget *</Label>
                  <Select
                    value={formData.budget}
                    onValueChange={(value) => handleSelectChange('budget', value)}
                    required
                  >
                    <SelectTrigger id="budget" data-testid="lead-budget-select" className="h-12">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetRanges.map((range) => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <Label htmlFor="additional_info" className="text-text-primary">Project Details</Label>
                <Textarea
                  id="additional_info"
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us about your project..."
                  className="resize-none"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-text-primary">Full Name *</Label>
                  <Input
                    id="full_name"
                    type="text"
                    name="full_name"
                    data-testid="lead-name-input"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="work_email" className="text-text-primary">Work Email *</Label>
                  <Input
                    id="work_email"
                    type="email"
                    name="work_email"
                    data-testid="lead-email-input"
                    value={formData.work_email}
                    onChange={handleChange}
                    required
                    placeholder="john@company.com"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <Label htmlFor="phone" className="text-text-primary">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="h-12"
                />
              </div>

              <Button
                type="submit"
                data-testid="lead-submit-btn"
                disabled={loading}
                className="w-full bg-primary text-white hover:bg-primary-hover rounded-full h-14 text-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Hire Developers Now
                    <Send className="ml-2 h-5 w-5" aria-hidden="true" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden" aria-labelledby="cta-heading">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 h-64 w-64 rounded-full bg-primary blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-secondary blur-3xl" />
      </div>
      
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 id="cta-heading" className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Ready to Build Your App or Website?
          </h2>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            Get a free consultation from our software development experts. 
            We help businesses build custom software solutions that drive growth.
          </p>
          <Link to="/contact" onClick={scrollToTop}>
            <Button
              data-testid="cta-contact-btn"
              className="bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-6 text-lg shadow-lg"
            >
              Get Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Main HomePage Component
const HomePage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Plutops | Enterprise IT & Digital Engineering';
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="home-page">
      <PageSEO 
        title={SEO_CONFIG.home.title}
        description={SEO_CONFIG.home.description}
        keywords={SEO_CONFIG.home.keywords}
        canonical="/"
      />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }]} />
      
      <HeroSection />
      <ServicesSection services={services} loading={loading} />
      <GlobalReachSection />
      <HowWeWorkSection />
      <LeadCaptureSection />
      <CTASection />
    </div>
  );
};

export default HomePage;
